import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

interface OrderData {
  id: string;
  product?: { name: string; price: number; image: string };
  items?: { name: string; price: number; image: string; quantity: number; size?: string; selectedColor?: { name: string; hex: string } }[];
  orderDate: string;
  status: string;
  total?: number;
  final_amount?: number;
}

interface AddressData {
  fullName: string;
  address: string;
  city: string;
  pincode: string;
  lat?: number;
  lng?: number;
  phone?: string;
}

const DELIVERY_WINDOW_MINS = 30;

function getETA(orderDate: string): { mins: number; timeStr: string; isDelivered: boolean } {
  const placed = new Date(orderDate).getTime();
  const now = Date.now();
  const elapsed = Math.floor((now - placed) / 60000);
  const remaining = Math.max(0, DELIVERY_WINDOW_MINS - elapsed);
  const arrival = new Date(placed + DELIVERY_WINDOW_MINS * 60000);
  const hh = arrival.getHours();
  const mm = arrival.getMinutes().toString().padStart(2, '0');
  const ampm = hh >= 12 ? 'PM' : 'AM';
  return { mins: remaining, timeStr: `${hh % 12 || 12}:${mm} ${ampm}`, isDelivered: remaining === 0 };
}

// Calculate distance between two coordinates in km (Haversine formula)
function calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

const STEPS = ['Confirmed', 'Preparing', 'Delivery', 'Nearby', 'Delivered'];

export default function OrderTrackingWidget() {
  const navigate = useNavigate();
  const [order, setOrder] = useState<OrderData | null>(null);
  const [address, setAddress] = useState<AddressData | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [eta, setEta] = useState<{ mins: number; timeStr: string; isDelivered: boolean } | null>(null);
  const [progress, setProgress] = useState(60);
  const [stepIndex, setStepIndex] = useState(2);
  const widgetRef = useRef<HTMLDivElement>(null);

  // Nearby delivery notification states
  const [showNearbyPopup, setShowNearbyPopup] = useState(false);
  const [nearbyNotified, setNearbyNotified] = useState(false);
  const [deliveryBoyLocation, setDeliveryBoyLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [distance, setDistance] = useState<number | null>(null);
  const NEARBY_THRESHOLD_KM = 1; // 1km threshold
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Real delivery boy info from Supabase
  const [deliveryBoyName, setDeliveryBoyName] = useState<string | null>(null);
  const [deliveryBoyPhone, setDeliveryBoyPhone] = useState<string | null>(null);

  useEffect(() => {
    const raw = localStorage.getItem('currentOrder');
    if (!raw) return;
    try {
      const parsed: OrderData = JSON.parse(raw);
      const elapsed = (Date.now() - new Date(parsed.orderDate).getTime()) / 60000;
      if (elapsed >= DELIVERY_WINDOW_MINS) {
        localStorage.removeItem('currentOrder');
        return;
      }

      // Check Supabase for real order status — hide widget if cancelled or delivered
      supabase
        .from('orders')
        .select('status')
        .eq('id', parsed.id)
        .maybeSingle()
        .then(({ data }) => {
          if (data?.status === 'cancelled' || data?.status === 'delivered') {
            localStorage.removeItem('currentOrder');
            setOrder(null);
            return;
          }
          setOrder(parsed);
          const etaData = getETA(parsed.orderDate);
          setEta(etaData);
          if (elapsed < 2) setStepIndex(1);
          else if (elapsed < 25) setStepIndex(2);
          else if (elapsed < 28) setStepIndex(3);
          else setStepIndex(4);
        });
    } catch { /* ignore */ }

    const addrRaw = localStorage.getItem('userAddress');
    if (addrRaw) {
      try { setAddress(JSON.parse(addrRaw)); } catch { /* ignore */ }
    }
  }, []);

  useEffect(() => {
    if (!order) return;
    const t = setInterval(async () => {
      const elapsed = (Date.now() - new Date(order.orderDate).getTime()) / 60000;

      // Check if order was cancelled or delivered by admin
      const { data } = await supabase
        .from('orders')
        .select('status')
        .eq('id', order.id)
        .maybeSingle();

      if (data?.status === 'cancelled' || data?.status === 'delivered') {
        localStorage.removeItem('currentOrder');
        setOrder(null);
        return;
      }

      const etaData = getETA(order.orderDate);
      setEta(etaData);
      if (elapsed < 2) setStepIndex(1);
      else if (elapsed < 25) setStepIndex(2);
      else if (elapsed < 28) setStepIndex(3);
      else setStepIndex(4);
      // Auto-close widget when 30 min timer ends (time-based delivery)
      if (elapsed >= DELIVERY_WINDOW_MINS) {
        localStorage.removeItem('currentOrder');
        setOrder(null);
        setIsExpanded(false);
      }
    }, 10000);
    return () => clearInterval(t);
  }, [order]);

  useEffect(() => {
    if (!isExpanded) return;
    const t = setInterval(() => setProgress(p => p >= 90 ? 62 : p + 0.5), 2000);
    return () => clearInterval(t);
  }, [isExpanded]);

  // Poll delivery boy location and check if nearby (within 1km)
  useEffect(() => {
    if (!order || !address?.lat || !address?.lng) return;

    const checkDeliveryBoyLocation = async () => {
      const { data, error } = await supabase
        .from('orders')
        .select('delivery_lat, delivery_lng, status, delivery_boy_name, delivery_boy_phone')
        .eq('id', order.id)
        .maybeSingle();

      if (error || !data) return;

      // Stop checking if order is delivered or cancelled
      if (data.status === 'delivered' || data.status === 'cancelled') {
        setShowNearbyPopup(false);
        return;
      }

      // Update delivery boy info
      if (data.delivery_boy_name) setDeliveryBoyName(data.delivery_boy_name);
      if (data.delivery_boy_phone) setDeliveryBoyPhone(data.delivery_boy_phone);

      // If delivery boy location available
      if (data.delivery_lat && data.delivery_lng) {
        const boyLat = data.delivery_lat;
        const boyLng = data.delivery_lng;
        setDeliveryBoyLocation({ lat: boyLat, lng: boyLng });

        // Calculate distance
        const dist = calculateDistance(address.lat!, address.lng!, boyLat, boyLng);
        setDistance(dist);

        // If within 1km and not yet notified
        if (dist <= NEARBY_THRESHOLD_KM && !nearbyNotified) {
          setNearbyNotified(true);
          setShowNearbyPopup(true);
          setStepIndex(3); // Update step to "Nearby"

          // Play notification sound
          try {
            if (!audioRef.current) {
              audioRef.current = new Audio('https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3');
            }
            audioRef.current.play().catch(() => {
              // Ignore autoplay errors
            });
          } catch {
            // Ignore audio errors
          }
        }
      }
    };

    // Check immediately and then every 10 seconds
    checkDeliveryBoyLocation();
    const interval = setInterval(checkDeliveryBoyLocation, 10000);

    return () => clearInterval(interval);
  }, [order, address, nearbyNotified]);

  if (!order) return null;

  const orderName = order.product?.name
    ?? (order.items && order.items.length > 0
      ? `${order.items[0].name}${order.items.length > 1 ? ` +${order.items.length - 1} more` : ''}`
      : 'Your Order');
  const orderImage = order.product?.image ?? order.items?.[0]?.image;
  const hasGPS = !!(address?.lat && address?.lng);
  const total = order.final_amount ?? order.total ?? order.product?.price ?? order.items?.reduce((s, i) => s + i.price * i.quantity, 0) ?? 0;

  // Close nearby popup handler
  const handleCloseNearbyPopup = () => {
    setShowNearbyPopup(false);
  };

  return (
    <div
      ref={widgetRef}
      className="fixed bottom-6 left-4 right-4 z-50 flex justify-center pointer-events-none"
    >
      <div
        className="pointer-events-auto w-full max-w-sm"
        style={{ filter: 'drop-shadow(0 8px 32px rgba(0,0,0,0.18))' }}
        onTouchMove={(e) => { e.stopPropagation(); }}
        onWheel={(e) => { e.stopPropagation(); }}
      >

        {/* ── Nearby Delivery Popup (Sound + Alert) ── */}
        {showNearbyPopup && (
          <div className="mb-3 animate-slide-up">
            <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl p-4 text-white shadow-lg border-2 border-white">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center shrink-0 animate-pulse">
                  <i className="ri-notification-3-fill text-white text-2xl"></i>
                </div>
                <div className="flex-1">
                  <p className="font-bold text-base mb-1 flex items-center gap-2">
                    <i className="ri-e-bike-2-fill"></i>
                    Delivery Boy Nearby!
                  </p>
                  <p className="text-white/90 text-sm leading-relaxed">
                    Your order will arrive in a few minutes. Please keep <strong>₹{total}</strong> amount ready.
                  </p>
                  <p className="text-white/80 text-xs mt-2 flex items-center gap-1">
                    <i className="ri-t-shirt-line"></i>
                    You can also choose any size that fits you!
                  </p>
                  {distance !== null && (
                    <p className="text-white/70 text-xs mt-1">
                      Distance: {distance.toFixed(2)} km away
                    </p>
                  )}
                </div>
                <button
                  onClick={handleCloseNearbyPopup}
                  className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors shrink-0"
                >
                  <i className="ri-close-line text-white"></i>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Collapsed Pill ── */}
        {!isExpanded && (
          <div
            onClick={() => setIsExpanded(true)}
            className="cursor-pointer bg-white border border-gray-200 rounded-2xl px-4 py-3 flex items-center gap-3 animate-slide-up"
          >
            <div className="relative shrink-0">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-primary-purple to-primary-pink flex items-center justify-center">
                <i className="ri-e-bike-2-fill text-white text-lg"></i>
              </div>
              <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-white animate-pulse"></span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-gray-900 font-semibold text-sm leading-tight">Order On The Way!</p>
              <p className="text-gray-500 text-xs truncate">
                {eta ? `~${eta.mins} min · ${eta.timeStr}` : 'Tracking live...'}
              </p>
            </div>
            <span className="text-xs text-primary-pink font-semibold whitespace-nowrap flex items-center gap-1 shrink-0">
              <i className="ri-map-pin-2-line"></i>Track
              <i className="ri-arrow-up-s-line"></i>
            </span>
          </div>
        )}

        {/* ── Expanded Card ── */}
        {isExpanded && (
          <div
            className="bg-white border border-gray-200 rounded-2xl overflow-hidden animate-slide-up"
            onTouchMove={(e) => e.stopPropagation()}
            onWheel={(e) => e.stopPropagation()}
          >

            {/* Header */}
            <div className="px-4 pt-4 pb-3 flex items-center gap-3 border-b border-gray-100">
              <div className="relative shrink-0">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-primary-purple to-primary-pink flex items-center justify-center animate-bounce">
                  <i className="ri-e-bike-2-fill text-white text-lg"></i>
                </div>
                <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-white animate-pulse"></span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-gray-900 font-bold text-sm">Order On The Way!</p>
                  <span className="flex items-center gap-1 text-[10px] text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>LIVE
                  </span>
                </div>
                <p className="text-gray-400 text-xs truncate">{order.id}</p>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); setIsExpanded(false); }}
                className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors cursor-pointer shrink-0"
                title="Minimize"
              >
                <i className="ri-arrow-down-s-line text-gray-600"></i>
              </button>
            </div>

            {/* ETA Banner */}
            {eta?.isDelivered ? (
              <div className="mx-4 mt-3 mb-3 bg-green-50 border border-green-200 rounded-xl px-4 py-3 flex items-center justify-center gap-3">
                <i className="ri-checkbox-circle-fill text-green-600 text-2xl"></i>
                <div>
                  <p className="text-green-700 font-bold text-base">Order Delivered!</p>
                  <p className="text-gray-500 text-xs">Hope you love it!</p>
                </div>
              </div>
            ) : (
              <div className="mx-4 mt-3 mb-3 bg-orange-50 border border-orange-200 rounded-xl px-4 py-2.5 flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-xs">Estimated Arrival</p>
                  <p className="text-gray-900 font-bold text-lg leading-tight">{eta?.timeStr ?? '--:--'}</p>
                </div>
                <div className="text-right">
                  <p className="text-gray-500 text-xs">Time Left</p>
                  <p className="text-primary-pink font-bold text-lg leading-tight">~{eta?.mins ?? '?'} min</p>
                </div>
              </div>
            )}

            {/* Step Progress */}
            <div className="px-4 mb-3">
              <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden mb-2">
                <div
                  className="h-full bg-gradient-to-r from-primary-purple to-primary-pink rounded-full"
                  style={{ width: `${progress}%`, transition: 'width 2s ease-in-out' }}
                ></div>
              </div>
              <div className="flex items-center justify-between">
                {STEPS.map((s, i) => (
                  <span key={s} className={`text-[9px] font-semibold ${
                    i < stepIndex ? 'text-green-600' : i === stepIndex ? 'text-primary-pink' : 'text-gray-400'
                  }`}>
                    {i < stepIndex ? '✓' : s}
                  </span>
                ))}
              </div>
            </div>

            {/* Map */}
            {hasGPS && (
              <div className="mx-4 mb-3 rounded-xl overflow-hidden border border-gray-200 relative" style={{ height: '130px' }}>
                <iframe
                  src={`https://maps.google.com/maps?q=${address!.lat},${address!.lng}&z=15&output=embed`}
                  width="100%" height="130"
                  style={{ border: 0, pointerEvents: 'none' }}
                  loading="lazy" title="Delivery Location"
                ></iframe>
                {/* Transparent overlay to block iframe touch/scroll events */}
                <div className="absolute inset-0" style={{ zIndex: 1 }} />
                <div
                  className="absolute pointer-events-none"
                  style={{
                    left: `${25 + progress * 0.35}%`,
                    top: `${35 + Math.sin(progress * 0.08) * 12}%`,
                    transition: 'left 3s ease-in-out, top 3s ease-in-out',
                    zIndex: 2,
                  }}
                >
                  <div className="relative">
                    <div className="w-8 h-8 rounded-full bg-primary-pink border-2 border-white shadow-lg flex items-center justify-center">
                      <i className="ri-e-bike-2-fill text-white text-xs"></i>
                    </div>
                    <div className="absolute inset-0 rounded-full bg-primary-pink/30 animate-ping"></div>
                  </div>
                </div>
                <div className="absolute bottom-4 right-6 pointer-events-none" style={{ zIndex: 2 }}>
                  <div className="w-7 h-7 rounded-full bg-green-500 border-2 border-white shadow-lg flex items-center justify-center">
                    <i className="ri-home-5-fill text-white text-xs"></i>
                  </div>
                </div>
              </div>
            )}

            {/* Address */}
            {address && (
              <div className="mx-4 mb-3 flex items-start gap-3 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5">
                <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center shrink-0 mt-0.5">
                  <i className="ri-map-pin-fill text-green-600 text-sm"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-gray-900 text-xs font-semibold">{address.fullName}</p>
                  <p className="text-gray-500 text-xs truncate">{address.address}, {address.city} {address.pincode}</p>
                  {address.phone && <p className="text-gray-400 text-xs mt-0.5"><i className="ri-phone-line mr-1"></i>{address.phone}</p>}
                </div>
                {hasGPS && <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full shrink-0 font-medium">GPS</span>}
              </div>
            )}

            {/* Product */}
            <div className="mx-4 mb-3 flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2">
              {orderImage && <img src={orderImage} alt={orderName} className="w-10 h-12 object-cover rounded-lg shrink-0" />}
              <div className="flex-1 min-w-0">
                <p className="text-gray-900 text-xs font-semibold truncate">{orderName}</p>
                <p className="text-gray-500 text-xs">Cash on Delivery</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-primary-pink font-bold text-sm">₹{total}</p>
                <p className="text-gray-400 text-[10px]">COD</p>
              </div>
            </div>

            {/* Delivery Partner */}
            {deliveryBoyName && (
              <div className="mx-4 mb-3 flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center shrink-0 border border-gray-200">
                  <i className="ri-e-bike-2-fill text-white text-sm"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-gray-900 text-xs font-semibold truncate">{deliveryBoyName}</p>
                  <div className="flex items-center gap-1">
                    <i className="ri-map-pin-2-line text-orange-400 text-[10px]"></i>
                    <span className="text-gray-500 text-[10px]">Delivery Partner</span>
                  </div>
                </div>
                {deliveryBoyPhone ? (
                  <a
                    href={`tel:${deliveryBoyPhone}`}
                    onClick={(e) => e.stopPropagation()}
                    className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center hover:bg-green-200 transition-colors shrink-0 cursor-pointer"
                    title={`Call ${deliveryBoyName}`}
                  >
                    <i className="ri-phone-fill text-green-600 text-sm"></i>
                  </a>
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                    <i className="ri-phone-line text-gray-400 text-sm"></i>
                  </div>
                )}
              </div>
            )}

            {/* Full Tracking Button */}
            <div className="px-4 pb-4">
              <button
                onClick={() => navigate('/profile')}
                className="w-full py-2.5 rounded-xl bg-gradient-to-r from-primary-purple to-primary-pink text-white font-bold text-sm flex items-center justify-center gap-2 hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap"
              >
                <i className="ri-map-2-line"></i>
                View Full Tracking
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
